import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { CalendarRange, EllipsisVertical } from 'lucide-react'
import { type MouseEvent, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router'

import { getHighestRole, isApproverOrHigherRole } from '@ankor-io/common/domain/iam/account'
import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { Role } from '@ankor-io/common/types/account'
import { CharterStatus, type CharterSummary, type UpdateCharterRequest } from '@ankor-io/common/types/charter'
import { formatCurrency } from '@ankor-io/common/util/currencies'
import { formatDate } from '@ankor-io/common/util/dates'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/charter'

import { useCharter } from '@/hooks/useCharter'
import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import Loading from '@/ui-components/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'
import ConfirmationModal from '@/views/components/ConfirmationModal'

import { CharterFilters, type CharterFiltersState } from './CharterFilters'

const CHARTERS_PER_PAGE = 100
const SKIP = 0

export const ChartersTable = () => {
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { setCharter } = useCharter()
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState<CharterFiltersState>({
    status: '',
    startDate: '',
    endDate: '',
  })

  const [charters, setCharters] = useState<CharterSummary[]>([])
  const [isStartingCharter, setIsStartingCharter] = useState(false)
  const [isCancellingCharter, setIsCancellingCharter] = useState(false)
  const [isCompletingCharter, setIsCompletingCharter] = useState(false)
  const [selectedCharter, setSelectedCharter] = useState<CharterSummary | null>(null)
  const [isCancelCharterConfirmationModalOpen, setIsCancelCharterConfirmationModalOpen] = useState(false)

  useEffect(() => {
    const getCharters = async () => {
      try {
        const queryParams = new URLSearchParams()
        queryParams.set('take', CHARTERS_PER_PAGE.toString())
        queryParams.set('skip', SKIP.toString())

        if (filters.status) {
          queryParams.set('status', filters.status)
        }

        if (filters.startDate) {
          queryParams.set('startDateFrom', new Date(filters.startDate).toISOString())
          // Not sure what to do with startDateTo
        }

        if (filters.endDate) {
          // Not sure what to do with endDateFrom
          queryParams.set('endDateTo', new Date(filters.endDate).toISOString())
        }

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charters/search?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch charters')
        }

        const data: CharterSummary[] = await res.json()
        setCharters(data || [])
      } catch (error) {
        console.error('Error fetching charters:', error)
        // Don't throw error, just set empty array and continue
        setCharters([])
      } finally {
        setIsLoading(false)
      }
    }

    getCharters()
  }, [activeConnectedAccountId, activeOrganizationId, getAccessToken, filters])

  const handleFilterChange = (key: string, value: string) => {
    const newValue = key === 'status' ? (value === '' ? '' : (value as CharterStatus)) : value
    setFilters((prev) => ({
      ...prev,
      [key]: newValue,
    }))
  }

  const handleStartCharter = (charter: CharterSummary) => {
    const startCharter = async () => {
      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsStartingCharter(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.IN_PROGRESS,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to start charter.')
        }

        const data: CharterSummary = await res.json()
        setCharters((prev) => prev?.map((charter) => (charter.id === data.id ? data : charter)) || [])
      } catch (error) {
        console.error('Error starting charter:', error)
        throw new Error('Failed to start charter.')
      } finally {
        setIsStartingCharter(false)
      }
    }

    toast.promise(startCharter, {
      loading: 'Starting charter...',
      success: 'Charter started successfully.',
      error: 'Failed to start charter.',
    })
  }

  const handleCancelCharter = (charter: CharterSummary) => {
    const cancelCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsCancellingCharter(true)

      try {
        // Convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.CANCELLED,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to cancel charter.')
        }

        const data: CharterSummary = await res.json()
        setCharters((prev) => prev?.map((charter) => (charter.id === data.id ? data : charter)) || [])
      } catch (error) {
        console.error('Error cancelling charter:', error)
        throw new Error('Failed to cancel charter.')
      } finally {
        setIsCancelCharterConfirmationModalOpen(false)
        setIsCancellingCharter(false)
      }
    }

    toast.promise(cancelCharter, {
      loading: 'Cancelling charter...',
      success: 'Charter cancelled successfully.',
      error: 'Failed to cancel charter.',
    })
  }

  const handleCompleteCharter = (charter: CharterSummary) => {
    const completeCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsCompletingCharter(true)

      try {
        // Convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.COMPLETED,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to complete charter.')
        }

        const data: CharterSummary = await res.json()
        setCharters((prev) => prev?.map((charter) => (charter.id === data.id ? data : charter)) || [])
      } catch (error) {
        console.error('Error completing charter:', error)
        throw new Error('Failed to complete charter.')
      } finally {
        setIsCompletingCharter(false)
      }
    }

    toast.promise(completeCharter, {
      loading: 'Completing charter...',
      success: 'Charter completed successfully.',
      error: 'Failed to complete charter.',
    })
  }

  const goToCharterDetail = (charter: CharterSummary) => {
    // Viewers can't access charter detail page
    if (getHighestRole([activeOrganizationRole]) === Role.VIEWER) {
      return
    }

    setCharter(charter)
    navigate(`/charters/${charter.id}`)
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <CharterFilters
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Charter Table */}
      <div className="overflow-x-auto">
        <Table className="max-w-[calc(100vw-3rem)] lg:max-w-[calc(100vw-20rem)]">
          <TableHead>
            <TableRow>
              <TableHeader>Charter Name</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Start Date</TableHeader>
              <TableHeader>End Date</TableHeader>
              <TableHeader>Currency</TableHeader>
              <TableHeader>APA Amount</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {charters.map((charter) => (
              <TableRow
                key={charter.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                onClick={() => goToCharterDetail(charter)}
              >
                <TableCell>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-50">{charter.name}</span>
                </TableCell>
                <TableCell>
                  <Badge color={getStatusColour(charter.status)}>{getStatusText(charter.status)}</Badge>
                </TableCell>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(charter.startDate)}
                </TableCell>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(charter.endDate)}
                </TableCell>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">{charter.currencyCode}</TableCell>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                  {formatCurrency(fromCents(charter.apaCents), charter.currencyCode)}
                </TableCell>
                <TableCell>
                  {charter.status !== CharterStatus.COMPLETED && charter.status !== CharterStatus.CANCELLED && (
                    <div className="relative z-10 pointer-events-auto">
                      <Dropdown>
                        <DropdownButton
                          plain
                          className="!py-0 !px-2"
                          onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                          <EllipsisVertical data-slot="icon" />
                        </DropdownButton>
                        {/* All buttons disabled if just 1 of these is clicked as they should not be able to run in parallel as the state is affected */}
                        <DropdownMenu anchor="bottom end">
                          {/* Start Charter */}
                          {charter.status === CharterStatus.UPCOMING && (
                            <DropdownItem
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={isStartingCharter || isCancellingCharter}
                              onClick={(e: MouseEvent) => {
                                e.stopPropagation()
                                handleStartCharter(charter)
                              }}
                              data-charter-status={charter.status}
                              data-action-type="start-charter"
                            >
                              {isStartingCharter ? 'Starting charter...' : 'Start Charter'}
                            </DropdownItem>
                          )}

                          {/* Edit Charter */}
                          {(charter.status !== CharterStatus.SIGNING_OFF ||
                            isApproverOrHigherRole(activeOrganizationRole)) && (
                            <DropdownItem
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={isStartingCharter || isCancellingCharter}
                              onClick={(e: MouseEvent) => {
                                e.stopPropagation()
                                setCharter(charter)
                                navigate(`/charters/${charter.id}/edit`)
                              }}
                              data-charter-status={charter.status}
                              data-action-type="edit-charter"
                            >
                              Edit Charter
                            </DropdownItem>
                          )}

                          {/* Sign-off Charter */}
                          {charter.status === CharterStatus.IN_PROGRESS &&
                            isApproverOrHigherRole(activeOrganizationRole) && (
                              <DropdownItem
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isStartingCharter || isCancellingCharter}
                                onClick={(e: MouseEvent) => {
                                  e.stopPropagation()
                                  setCharter(charter)
                                  navigate(`/charters/${charter.id}/sign-off`)
                                }}
                                data-charter-status={charter.status}
                                data-action-type="sign-off-charter"
                              >
                                Sign-off Charter
                              </DropdownItem>
                            )}

                          {/* Cancel Charter */}
                          {isApproverOrHigherRole(activeOrganizationRole) && (
                            <DropdownItem
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={isStartingCharter || isCancellingCharter}
                              onClick={(e: MouseEvent) => {
                                e.stopPropagation()
                                setSelectedCharter(charter)
                                setIsCancelCharterConfirmationModalOpen(true)
                              }}
                              data-charter-status={charter.status}
                              data-action-type="cancel-charter"
                            >
                              Cancel Charter
                            </DropdownItem>
                          )}

                          {/* Complete Charter */}
                          {isApproverOrHigherRole(activeOrganizationRole) &&
                            charter.status === CharterStatus.SIGNING_OFF && (
                              <DropdownItem
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isCompletingCharter}
                                onClick={(e: MouseEvent) => {
                                  e.stopPropagation()
                                  handleCompleteCharter(charter)
                                }}
                                data-charter-status={charter.status}
                                data-action-type="complete-charter"
                              >
                                Complete Charter
                              </DropdownItem>
                            )}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : (
        charters.length === 0 && (
          <div className="text-center py-12">
            <CalendarRange className="mx-auto size-12 text-gray-400" />
            <span className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No charters found</span>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {filters.status === '' && filters.startDate === '' && filters.endDate === ''
                ? isApproverOrHigherRole(activeOrganizationRole)
                  ? 'Get started by creating a new charter.'
                  : 'Please contact your administrator to create a new charter.'
                : 'Try adjusting your filters.'}
            </p>
          </div>
        )
      )}

      {selectedCharter && isCancelCharterConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isCancelCharterConfirmationModalOpen}
          onClose={() => setIsCancelCharterConfirmationModalOpen(false)}
          title="Cancel Charter"
          description="Are you sure you want to cancel this charter? You won't be able to attribute any expense to this charter anymore."
          confirmText="Cancel Charter"
          confirmLoadingText="Cancelling charter..."
          confirmColour="red"
          isLoading={isCancellingCharter}
          onConfirm={() => handleCancelCharter(selectedCharter)}
        />
      )}
    </div>
  )
}
